/**
 * The header strip.
 *
 * Follows the layout: the hamburger, business name, outlet selector, fiscal
 * status pill, Hold / Recall, shift badge, notification bell, fullscreen
 * toggle, and the user menu.
 *
 * That is more than fits on a phone, so the strip sheds controls as it narrows
 * - but sheds them into the user menu rather than out of the app. Hold, Recall,
 * the outlet selector and the fiscal state are all reachable at every width;
 * only the shape changes. What is genuinely pointless small is dropped outright
 * (fullscreen, which a phone browser does not meaningfully have).
 *
 * The hamburger means two different things by width, which is deliberate: from
 * lg up it collapses the sidebar that is already on screen, and below lg it
 * opens the drawer that stands in for it.
 */

import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    FiMenu, FiPause, FiClock, FiBell, FiMaximize, FiMinimize,
    FiWifi, FiWifiOff, FiLogOut, FiUser, FiChevronDown, FiMapPin
} from 'react-icons/fi';
import toast from 'react-hot-toast';

import {
    toggleSidebar, toggleMobileNav, openModal, selectFiscal, selectShift
} from '../store/uiSlice';
import { logout, selectUser } from '../store/authSlice';
import { selectBusiness, selectMenu } from '../store/menuSlice';
import { toggleFullscreen } from '../utils/print';
import { money } from '../utils/format';
import { useBreakpoint } from '../hooks/useMediaQuery';

/* Helper: initials from a display / user name */
const initials = (name = '') =>
    name.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

/** One row of the user dropdown. */
const MenuRow = ({ icon: Icon, label, onClick, className = '', iconWrap = 'bg-slate-100' }) => (
    <button
        type="button"
        onClick={onClick}
        className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm
                    transition-colors ${className}`}
    >
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${iconWrap}`}>
            <Icon className="h-3.5 w-3.5" />
        </span>
        {label}
    </button>
);

const TopBar = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const user = useSelector(selectUser);
    const business = useSelector(selectBusiness);
    const { outlets } = useSelector(selectMenu);
    const fiscal = useSelector(selectFiscal);
    const shift = useSelector(selectShift);
    const { isCompactNav } = useBreakpoint();

    const [isFullscreen, setIsFullscreen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);

    const onToggleFullscreen = async () => {
        const nowFullscreen = await toggleFullscreen();
        setIsFullscreen(!!nowFullscreen);
    };

    const onLogout = async () => {
        if (shift && !window.confirm('A shift is still open on this till. Sign out anyway?')) return;
        await dispatch(logout());
        navigate('/login');
    };

    const fiscalState = !fiscal.enabled
        ? { tone: 'off', Icon: FiWifiOff, label: 'Fiscal off' }
        : fiscal.failed > 0
            ? { tone: 'fail', Icon: FiWifiOff, label: `${fiscal.failed} failed` }
            : fiscal.pending > 0
                ? { tone: 'warn', Icon: FiWifi, label: `${fiscal.pending} queued` }
                : { tone: 'ok', Icon: FiWifi, label: 'Online' };

    const fiscalPillCls = {
        off: 'border-white/10 bg-white/5 text-white/35',
        fail: 'border-red-400/50 bg-red-500/20 text-red-300 hover:bg-red-500/30',
        warn: 'border-amber-400/50 bg-amber-500/20 text-amber-300 hover:bg-amber-500/30',
        ok: 'border-emerald-400/50 bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30',
    }[fiscalState.tone];

    // The pill's colour is the whole point of it, so the dot that replaces it
    // on a narrow screen has to carry the same four states.
    const fiscalDotCls = {
        off: 'bg-white/25',
        fail: 'bg-red-400',
        warn: 'bg-amber-400',
        ok: 'bg-emerald-400',
    }[fiscalState.tone];

    const userName = user?.displayName || user?.userName;

    return (
        <header
            className="flex h-14 shrink-0 items-center gap-1 px-2 sm:gap-2 sm:px-3 no-print"
            style={{ background: 'linear-gradient(135deg, #0f1f5c 0%, #152fe1 100%)' }}
        >
            {/* ── Hamburger ──────────────────────────────────────────── */}
            <button
                type="button"
                onClick={() => dispatch(isCompactNav ? toggleMobileNav() : toggleSidebar())}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg
                           text-white/70 transition-all hover:bg-white/15 hover:text-white lg:h-10 lg:w-10"
                aria-label={isCompactNav ? 'Open menu' : 'Collapse menu'}
            >
                <FiMenu className="h-5 w-5" />
            </button>

            {/* ── Business name ──────────────────────────────────────── */}
            {business && (
                <span className="min-w-0 truncate text-sm font-bold tracking-wide text-white/90">
                    {business.Business_Name}
                </span>
            )}

            <div className="hidden h-5 w-px shrink-0 bg-white/20 lg:block" />

            {/* ── Outlet selector ────────────────────────────────────── */}
            <div className="hidden shrink-0 items-center gap-2 lg:flex">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-white/40">
                    Outlet
                </span>
                <select
                    className="h-8 rounded-lg border border-white/20 bg-white/10 px-2
                               text-xs font-semibold text-white backdrop-blur-sm
                               focus:border-white/40 focus:outline-none focus:bg-white/15 transition-all"
                    defaultValue={outlets.find((o) => o.Is_Default)?.Outlet_ID || outlets[0]?.Outlet_ID}
                    disabled={outlets.length <= 1}
                    style={{ colorScheme: 'dark' }}
                >
                    {outlets.map((outlet) => (
                        <option
                            key={outlet.Outlet_ID}
                            value={outlet.Outlet_ID}
                            style={{ background: '#0f1f5c', color: '#fff' }}
                        >
                            {outlet.Outlet_Name}
                        </option>
                    ))}
                </select>
            </div>

            {/* ── Fiscal status pill ─────────────────────────────────── */}
            <button
                type="button"
                onClick={() => navigate('/fiscal')}
                title={
                    fiscal.enabled
                        ? `Reporting to ${fiscal.activeAuthorityCode || 'the authority'}`
                        : 'Fiscal reporting is switched off'
                }
                className={`hidden min-h-[2.25rem] shrink-0 items-center gap-1.5 rounded-full border px-3
                            py-2 text-[10px] font-bold uppercase tracking-wider transition-all
                            md:inline-flex lg:min-h-0 lg:py-1 ${fiscalPillCls}`}
            >
                <fiscalState.Icon className="h-3 w-3" />
                {fiscalState.label}
            </button>

            {/* ── Right-side controls ────────────────────────────────── */}
            <div className="ml-auto flex shrink-0 items-center gap-0.5">

                {/* Hold */}
                <button
                    type="button"
                    onClick={() => dispatch(openModal({ modal: 'hold' }))}
                    className="hidden h-8 items-center gap-1.5 rounded-lg px-3
                               text-[11px] font-semibold text-white/65
                               hover:bg-white/15 hover:text-white transition-all lg:inline-flex"
                >
                    <FiPause className="h-3.5 w-3.5" />
                    Hold
                </button>

                {/* Recall */}
                <button
                    type="button"
                    onClick={() => dispatch(openModal({ modal: 'recall' }))}
                    className="hidden h-8 items-center gap-1.5 rounded-lg px-3
                               text-[11px] font-semibold text-white/65
                               hover:bg-white/15 hover:text-white transition-all lg:inline-flex"
                >
                    <FiClock className="h-3.5 w-3.5" />
                    Recall
                </button>

                {/* Shift badge */}
                {shift && (
                    <div className="mr-1 hidden rounded-lg border border-emerald-400/40
                                    bg-emerald-500/15 px-3 py-1 xl:block">
                        <p className="text-[9px] font-bold uppercase tracking-wider text-emerald-300">
                            Shift open
                        </p>
                        <p className="tabular text-[11px] font-bold text-emerald-200">
                            Rs. {money(shift.Opening_Cash)} float
                        </p>
                    </div>
                )}

                {/* Bell */}
                <button
                    type="button"
                    onClick={() => {
                        if (fiscal.failed > 0) {
                            toast.error(`${fiscal.failed} invoice(s) were rejected. Open FBR / PRA to review.`);
                            navigate('/fiscal');
                        } else {
                            toast.success('Nothing needs your attention.');
                        }
                    }}
                    className="relative flex h-11 w-11 items-center justify-center rounded-lg
                               text-white/65 transition-all hover:bg-white/15 hover:text-white lg:h-10 lg:w-10"
                    aria-label="Notifications"
                >
                    <FiBell className="h-[18px] w-[18px]" />
                    {fiscal.failed > 0 && (
                        <span className="tabular absolute right-1.5 top-1.5 flex h-4 min-w-[1rem]
                                         items-center justify-center rounded-full bg-red-500
                                         px-1 text-[9px] font-black text-white shadow">
                            {fiscal.failed}
                        </span>
                    )}
                </button>

                {/* Fullscreen. A phone browser has no useful fullscreen, and the
                    room the button takes is worth more than the gesture. */}
                <button
                    type="button"
                    onClick={onToggleFullscreen}
                    className="hidden h-11 w-11 items-center justify-center rounded-lg
                               text-white/65 transition-all hover:bg-white/15 hover:text-white sm:flex lg:h-10 lg:w-10"
                    aria-label="Full screen"
                >
                    {isFullscreen
                        ? <FiMinimize className="h-4 w-4" />
                        : <FiMaximize className="h-4 w-4" />}
                </button>

                {/* Divider */}
                <div className="mx-1 hidden h-5 w-px bg-white/20 sm:block" />

                {/* ── User menu ──────────────────────────────────────── */}
                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setUserMenuOpen((v) => !v)}
                        className="flex h-11 items-center gap-2 rounded-lg px-1.5
                                   text-white/80 transition-all hover:bg-white/15 hover:text-white lg:h-10 lg:px-2"
                    >
                        {/* Avatar circle. The fiscal state rides on it wherever
                            the pill itself has been dropped. */}
                        <span className="relative flex h-7 w-7 shrink-0 items-center justify-center
                                         rounded-full bg-white/20 text-[11px] font-black text-white">
                            {initials(userName)}
                            <span
                                className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full
                                            ring-2 ring-[#132a8e] md:hidden ${fiscalDotCls}`}
                                title={fiscalState.label}
                            />
                        </span>
                        <span className="hidden max-w-[8rem] truncate text-xs font-semibold lg:inline">
                            {userName}
                        </span>
                        <FiChevronDown
                            className={`hidden h-3.5 w-3.5 transition-transform duration-200 sm:block
                                        ${userMenuOpen ? 'rotate-180' : ''}`}
                        />
                    </button>

                    {/* Dropdown card */}
                    {userMenuOpen && (
                        <>
                            <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />

                            <div className="absolute right-0 z-20 mt-2 w-[16rem] max-w-[calc(100vw-1rem)]
                                            overflow-hidden rounded-2xl
                                            border border-slate-200/80 bg-white animate-slide-up
                                            shadow-[0_20px_60px_rgba(15,23,42,0.18)]">

                                {/* Card header — same gradient */}
                                <div
                                    className="px-4 py-4"
                                    style={{ background: 'linear-gradient(135deg, #0f1f5c 0%, #152fe1 100%)' }}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="flex h-10 w-10 shrink-0 items-center justify-center
                                                         rounded-full bg-white/20 text-base font-black text-white">
                                            {initials(userName)}
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm font-bold text-white">
                                                {user?.displayName || user?.userName}
                                            </p>
                                            <p className="truncate text-[11px] capitalize text-white/55">
                                                {user?.roles?.join(', ')}
                                            </p>
                                        </div>
                                    </div>
                                    {business && (
                                        <p className="mt-2.5 truncate text-[10px] font-medium
                                                      uppercase tracking-wider text-white/35">
                                            {business.Business_Name}
                                        </p>
                                    )}
                                </div>

                                {/* ── What the strip could not fit ─────────
                                    Hidden from lg up, where each of these is a
                                    control of its own in the header. */}
                                <div className="border-b border-slate-100 py-1.5 lg:hidden">
                                    <MenuRow
                                        icon={FiPause}
                                        label="Hold this order"
                                        className="text-slate-700 hover:bg-brand-50 hover:text-brand-700"
                                        onClick={() => {
                                            setUserMenuOpen(false);
                                            dispatch(openModal({ modal: 'hold' }));
                                        }}
                                    />
                                    <MenuRow
                                        icon={FiClock}
                                        label="Recall a held order"
                                        className="text-slate-700 hover:bg-brand-50 hover:text-brand-700"
                                        onClick={() => {
                                            setUserMenuOpen(false);
                                            dispatch(openModal({ modal: 'recall' }));
                                        }}
                                    />
                                    <MenuRow
                                        icon={fiscalState.Icon}
                                        label={`FBR / PRA — ${fiscalState.label}`}
                                        className="text-slate-700 hover:bg-brand-50 hover:text-brand-700"
                                        onClick={() => {
                                            setUserMenuOpen(false);
                                            navigate('/fiscal');
                                        }}
                                    />

                                    {/* The outlet selector, as a real select so
                                        a multi-outlet shop can still switch. */}
                                    {outlets.length > 1 && (
                                        <div className="flex items-center gap-3 px-4 py-2.5">
                                            <span className="flex h-7 w-7 shrink-0 items-center justify-center
                                                             rounded-lg bg-slate-100">
                                                <FiMapPin className="h-3.5 w-3.5" />
                                            </span>
                                            <select
                                                className="input py-1.5 text-sm"
                                                defaultValue={
                                                    outlets.find((o) => o.Is_Default)?.Outlet_ID
                                                    || outlets[0]?.Outlet_ID
                                                }
                                            >
                                                {outlets.map((outlet) => (
                                                    <option key={outlet.Outlet_ID} value={outlet.Outlet_ID}>
                                                        {outlet.Outlet_Name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {/* The shift badge, which the strip only
                                        shows from xl. */}
                                    {shift && (
                                        <p className="px-4 py-2 text-[11px] font-semibold text-emerald-700">
                                            Shift open · Rs. {money(shift.Opening_Cash)} float
                                        </p>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="py-1.5">
                                    <MenuRow
                                        icon={FiUser}
                                        label="Change password or PIN"
                                        className="text-slate-700 hover:bg-brand-50 hover:text-brand-700"
                                        onClick={() => {
                                            setUserMenuOpen(false);
                                            dispatch(openModal({ modal: 'changePassword' }));
                                        }}
                                    />
                                </div>

                                <div className="border-t border-slate-100 py-1.5">
                                    <MenuRow
                                        icon={FiLogOut}
                                        label="Sign out"
                                        iconWrap="bg-red-50"
                                        className="text-red-600 hover:bg-red-50"
                                        onClick={onLogout}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
};

export default TopBar;
